import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import {
  EquipmentAsset,
  EquipmentAssetCreateInput,
  EquipmentAssetUpdateInput,
  MaterialCategory,
  CategoryType,
  Warehouse,
} from '@models/warehouse.model';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BuildIcon from '@mui/icons-material/Build';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface EquipmentAssetFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EquipmentAssetCreateInput | EquipmentAssetUpdateInput) => Promise<void>;
  equipment?: EquipmentAsset | null;
  categories: MaterialCategory[];
  warehouses?: Warehouse[];
  isLoading?: boolean;
}

export const EquipmentAssetFormModal: React.FC<EquipmentAssetFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  equipment,
  categories,
  warehouses = [],
  isLoading = false,
}) => {
  const isEditing = !!equipment;

  // Form state - Basic Info
  const [name, setName] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [assetCode, setAssetCode] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');

  // Form state - Purchase Info
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('');

  // Form state - Pricing
  const [hourlyRate, setHourlyRate] = useState('');
  const [clientHourlyRate, setClientHourlyRate] = useState('');

  // Form state - Location
  const [warehouseId, setWarehouseId] = useState('');

  // Filter categories to EQUIPMENT or BOTH
  const equipmentCategories = useMemo(() => {
    return categories.filter(
      (c) => c.type === CategoryType.EQUIPMENT || c.type === CategoryType.BOTH
    );
  }, [categories]);

  // Calculate margin preview
  const hourlyMargin = useMemo(() => {
    const cost = parseFloat(hourlyRate) || 0;
    const price = parseFloat(clientHourlyRate) || 0;
    if (cost > 0 && price > 0) {
      return (((price - cost) / cost) * 100).toFixed(1);
    }
    return null;
  }, [hourlyRate, clientHourlyRate]);

  // Reset form when modal opens/closes or equipment changes
  useEffect(() => {
    if (isOpen) {
      if (equipment) {
        setName(equipment.name || '');
        setSerialNumber(equipment.serialNumber || '');
        setAssetCode(equipment.assetCode || '');
        setCategoryId(equipment.categoryId || '');
        setDescription(equipment.description || '');
        setManufacturer(equipment.manufacturer || '');
        setModel(equipment.model || '');
        setPurchaseDate(equipment.purchaseDate ? new Date(equipment.purchaseDate).toISOString().split('T')[0] : '');
        setPurchaseCost(equipment.purchaseCost?.toString() || '');
        setHourlyRate(equipment.hourlyRate?.toString() || '');
        setClientHourlyRate(equipment.clientHourlyRate?.toString() || '');
        setWarehouseId(equipment.warehouseId || '');
      } else {
        setName('');
        setSerialNumber('');
        setAssetCode('');
        setCategoryId('');
        setDescription('');
        setManufacturer('');
        setModel('');
        setPurchaseDate('');
        setPurchaseCost('');
        setHourlyRate('');
        setClientHourlyRate('');
        setWarehouseId(warehouses.find(w => w.isDefault)?.warehouseId || '');
      }
    }
  }, [isOpen, equipment, warehouses]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    const baseData = {
      name: name.trim(),
      assetCode: assetCode.trim() || undefined,
      description: description.trim() || undefined,
      serialNumber: serialNumber.trim() || undefined,
      manufacturer: manufacturer.trim() || undefined,
      model: model.trim() || undefined,
      categoryId: categoryId || undefined,
      purchaseCost: purchaseCost ? parseFloat(purchaseCost) : undefined,
      purchaseDate: purchaseDate || undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      clientHourlyRate: clientHourlyRate ? parseFloat(clientHourlyRate) : undefined,
      warehouseId: warehouseId || undefined,
    };

    if (isEditing && equipment) {
      await onSubmit({ assetId: equipment.assetId, ...baseData });
    } else {
      await onSubmit(baseData as EquipmentAssetCreateInput);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Equipment' : 'Add New Equipment'}
      confirmText={isEditing ? 'Save Changes' : 'Add Equipment'}
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      disableConfirm={!name.trim()}
      size="large"
    >
      <div className="flex flex-col gap-6">
        {/* Basic Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <InfoOutlinedIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Name *" htmlFor="equipmentName">
              <Input
                id="equipmentName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., DeWalt Circular Saw"
                autoFocus
              />
            </FormGroup>

            <FormGroup label="Serial Number" htmlFor="equipmentSerial">
              <Input
                id="equipmentSerial"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="e.g., SN-2024-001"
              />
            </FormGroup>

            <FormGroup label="Asset Code" htmlFor="equipmentAssetCode">
              <Input
                id="equipmentAssetCode"
                value={assetCode}
                onChange={(e) => setAssetCode(e.target.value)}
                placeholder="e.g., EQ-001"
              />
              <p className="mt-1 text-xs text-gray-500">Leave empty to auto-generate</p>
            </FormGroup>

            <FormGroup label="Category" htmlFor="equipmentCategory">
              <ReactSelect
                options={equipmentCategories.map((cat) => ({
                  value: cat.categoryId,
                  label: cat.name,
                }))}
                value={
                  categoryId
                    ? equipmentCategories
                        .filter((cat) => cat.categoryId === categoryId)
                        .map((cat) => ({ value: cat.categoryId, label: cat.name }))[0] || null
                    : null
                }
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setCategoryId(option.value as string);
                  } else {
                    setCategoryId('');
                  }
                }}
                placeholder="Select category..."
                isClearable
              />
            </FormGroup>

            <FormGroup label="Manufacturer" htmlFor="equipmentManufacturer">
              <Input
                id="equipmentManufacturer"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                placeholder="e.g., DeWalt"
              />
            </FormGroup>

            <FormGroup label="Model" htmlFor="equipmentModel">
              <Input
                id="equipmentModel"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="e.g., DWE575SB"
              />
            </FormGroup>

            <div className="md:col-span-2">
              <FormGroup label="Description" htmlFor="equipmentDescription">
                <textarea
                  id="equipmentDescription"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Additional details about this equipment..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  rows={2}
                />
              </FormGroup>
            </div>
          </div>
        </div>

        {/* Purchase Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AttachMoneyIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Purchase Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Purchase Date" htmlFor="equipmentPurchaseDate">
              <Input
                id="equipmentPurchaseDate"
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
            </FormGroup>

            <FormGroup label="Purchase Cost" htmlFor="equipmentPurchaseCost">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  id="equipmentPurchaseCost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={purchaseCost}
                  onChange={(e) => setPurchaseCost(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-md py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </FormGroup>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AttachMoneyIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Rates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Cost per Hour" htmlFor="equipmentHourlyRate">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  id="equipmentHourlyRate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-md py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Internal cost per hour of use</p>
            </FormGroup>

            <FormGroup label="Client Price per Hour" htmlFor="equipmentClientHourlyRate">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  id="equipmentClientHourlyRate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={clientHourlyRate}
                  onChange={(e) => setClientHourlyRate(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-md py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Price charged to clients per hour</p>
            </FormGroup>

          </div>

          {/* Margin Preview */}
          {hourlyMargin && (
            <div className="mt-4 p-3 bg-white rounded-md border border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Estimated Hourly Margin:</span>
                <span className={`font-medium ${parseFloat(hourlyMargin) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {hourlyMargin}%
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Location & Storage */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <LocationOnIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Location & Storage
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Warehouse" htmlFor="equipmentWarehouse">
              <ReactSelect
                options={warehouses.map((wh) => ({
                  value: wh.warehouseId,
                  label: `${wh.name}${wh.isDefault ? ' (Default)' : ''}`,
                }))}
                value={
                  warehouseId
                    ? warehouses
                        .filter((wh) => wh.warehouseId === warehouseId)
                        .map((wh) => ({
                          value: wh.warehouseId,
                          label: `${wh.name}${wh.isDefault ? ' (Default)' : ''}`,
                        }))[0] || null
                    : null
                }
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setWarehouseId(option.value as string);
                  } else {
                    setWarehouseId('');
                  }
                }}
                placeholder="Select warehouse..."
                isClearable
              />
            </FormGroup>
          </div>
        </div>

        {/* Maintenance (edit only) */}
        {isEditing && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BuildIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
              Maintenance Info
            </h3>
            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex items-start gap-2">
                <InfoOutlinedIcon className="text-blue-500 mt-0.5" sx={{ fontSize: 16 }} />
                <p className="text-sm text-blue-700">
                  To set maintenance status or schedule, use the "Set Maintenance" action from the equipment list.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EquipmentAssetFormModal;
